import { useEffect, useState } from "react";

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const toggleItem = (key) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const faqSections = [
    {
      title: "Trust & Integrity",
      items: [
        {
          q: "Will you ever tell me something frightening to push me toward a remedy?",
          a: "No. This is perhaps our most fundamental commitment. We will never use fear as a tool. The Vedic tradition does not require fear — it requires honesty. If your chart shows a challenge, we will describe it calmly, in context, and explain what it means in practical terms. We will not dramatise, catastrophise, or use alarming language to pressure you.",
          honest: "We say this plainly because you have likely encountered astrologers who do exactly this. It is exploitation of a genuine spiritual tradition, and we consider it a serious ethical breach."
        },
        {
          q: "How do I know your rituals are actually performed and not just claimed?",
          a: "This is the most important question anyone can ask, and we are glad you are asking it. Every single ritual we perform — every Yagya, every Sankalpa, every Daan — is documented with video and photographs. You receive the recording. You will hear your name. You will see the ceremony. We do not ask for faith in what we cannot show you."
        },
        {
          q: "Are you going to keep asking me to pay for more and more rituals?",
          a: "No. We prescribe only what your chart indicates. If one remedy is sufficient, we will say so. We do not have targets. We do not earn commissions on ritual bookings. Our success is measured by the genuine relief our clients experience — not by the number of services we sell. If we feel something additional would genuinely help you, we will explain exactly why. You will always decide.",
          honest: "This is easy to claim. The evidence is in how we conduct ourselves. We encourage you to share your experience honestly — publicly or directly — after your consultation."
        },
        {
          q: "What if I am simply not sure I believe in astrology?",
          a: "That is a completely valid position, and we respect it. We do not require belief to work with us. Vedic astrology is a system of observation and interpretation — one that many people find meaningful and others approach with scepticism. We simply ask that you approach with openness. The chart will say what it says. You will assess whether it resonates. And you will decide entirely for yourself what, if anything, to do with it."
        }
      ]
    },
    {
      title: "Our Process",
      items: [
        {
          q: "What exactly happens after I place my order?",
          a: "You will receive an order confirmation with your unique Order ID. Our astrologer will study your birth chart within 24–48 hours. Your written analysis will be prepared. If a bracelet is part of your order, stones are selected based on your chart and the bracelet is Reiki-energised. Everything is shipped within 5–7 working days, and your documentation is delivered digitally. You can track your order status at any time using your Order ID."
        },
        {
          q: "What if I do not know my exact birth time?",
          a: "Birth time significantly affects chart accuracy, particularly for house placements. If you do not have an exact time, please provide your best approximation. Our astrologer will use a chart rectification approach — cross-referencing key life events — to narrow the birth time window. We will be transparent about the degree of certainty in our analysis based on available data."
        },
        {
          q: "Can I speak to someone before I commit to anything?",
          a: "Yes. We have a WhatsApp consultation option specifically for people who want to understand the process, ask preliminary questions, or discuss their situation before making any decision. There is no pressure and no obligation. We would rather you proceed with full confidence than proceed prematurely."
        }
      ]
    },
    {
      title: "Remedies & Rituals",
      items: [
        {
          q: "Are the results of rituals guaranteed?",
          a: "We will be completely honest: no. We do not guarantee outcomes, and we consider any astrologer who does to be making a claim they cannot support. What we can tell you is this — Vedic remedies are designed to create conditions more conducive to positive outcomes. They work in harmony with your own sincere effort, your circumstances, and the natural unfolding of events. We cannot control outcomes. We can only help you align more honestly with your own path.",
          honest: "We say this not to discourage you, but because we believe honesty about this is itself an act of respect. Anyone promising guaranteed results is exploiting your hope."
        },
        {
          q: "Are the stones in the bracelet genuinely natural and semi-precious?",
          a: "Yes, without exception. We source only natural, original semi-precious stones — never synthetic, never glass, never treated stones passed off as natural. Each bracelet comes with a description of every stone used, its planetary correspondence, and its care instructions. We would rather send fewer stones of genuine quality than more stones of questionable origin."
        },
        {
          q: "Who performs the Yagya ceremonies? Are they qualified?",
          a: "Our Yagya ceremonies are performed by Brahmins trained specifically in the lineage of each ritual type. A Navagraha Shanti Yagya requires different expertise than a Mahamrityunjaya Yagya, and we do not interchange practitioners. Each is verified in their specific tradition. We supervise the ceremony and document it in full — both to ensure quality and to provide you with the proof you deserve."
        }
      ]
    },
    {
      title: "Practical Matters",
      items: [
        {
          q: "What is your refund policy?",
          a: "If your order has not yet been processed (within 24 hours of placement), you may cancel and receive a full refund. Once the chart analysis has begun or a ritual has been initiated, we are unable to offer refunds — as the work has genuinely been performed. However, if you feel your analysis was not conducted with care, we ask that you contact us directly. We take every concern seriously and will review the case transparently."
        },
        {
          q: "Do you offer consultations outside India?",
          a: "Yes. Astro Analysis and digital services are available worldwide — birth chart is not bound by geography. International clients can receive their personalised Vedic chart analysis digitally. Please contact us before ordering so we can confirm arrangements."
        },
        {
          q: "Is my personal information kept confidential?",
          a: "Absolutely. Your birth data, the content of your consultation, and your personal information are never shared with any third party. They are used only for the purpose of your reading and the preparation of your remedies. We treat what you share with us with the same discretion a traditional counselor would — because that is exactly what we aim to be."
        }
      ]
    },
    {
      title: "About Astrology",
      items: [
        {
          q: "What is the difference between Vedic and Western astrology?",
          a: "Vedic astrology (Jyotish) uses the sidereal zodiac — based on the actual position of stars — while Western astrology uses the tropical zodiac based on seasons. Vedic astrology places significant emphasis on the Moon sign (rather than the Sun sign), on planetary periods (dashas), and on house placements for life area analysis. It is an extraordinarily precise system when practised rigorously, with a 5,000-year tradition of refinement."
        },
        {
          q: "Can astrology actually change what is destined to happen?",
          a: "The Vedic view is nuanced: some things are fixed (drida karma), some are malleable (adrida karma), and many lie between. Remedies do not override destiny — they help you navigate it more effectively, align your energy more coherently, and reduce the friction created by planetary imbalances. Think of it less as changing fate and more as improving your relationship with the conditions you face. That framing is more honest — and ultimately more empowering."
        }
      ]
    }
  ];

  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <div className="page-hero" data-testid="faq-hero">
        <div className="page-hero-content">
          <span className="page-hero-label">Honest Answers</span>
          <h1 className="page-hero-title">Questions, answered<br/><em>without evasion.</em></h1>
          <p className="page-hero-sub">These are the real questions people have before trusting us with something as personal as their life's challenges. We have answered them as honestly as we know how.</p>
        </div>
      </div>

      {/* FAQ Sections */}
      <section className="faq-page" data-testid="faq-content">
        <div className="wrap">
          {faqSections.map((section, sIdx) => (
            <div key={sIdx} className="faq-section reveal" data-testid={`faq-section-${sIdx}`}>
              <div className="faq-section-title">{section.title}</div>
              {section.items.map((item, iIdx) => {
                const key = `${sIdx}-${iIdx}`;
                return (
                  <div key={iIdx} className={`faq-item ${openItems[key] ? 'open' : ''}`} data-testid={`faq-item-${key}`}>
                    <div className="faq-q" onClick={() => toggleItem(key)}>
                      {item.q}
                      <span className="faq-toggle">+</span>
                    </div>
                    <div className="faq-a">
                      {item.a}
                      {item.honest && (
                        <div className="faq-honest">{item.honest}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
